/**
 * The page's one audio element for public previews (`PublicPreviewPlayer` owns it), so the tap that
 * plays a track can start it on that element directly.
 *
 * iOS starts audio only from inside a tap. The player starts a track from an effect, which React
 * runs before the tap's event has finished, but nothing promises that. So a row's Play, through
 * `useAuthenticatedPlayback`, also calls `startPublicPreviewInTap` right after setting the player's
 * track: when the track's preview was fetched ahead (a row asks for it as it comes on screen, see
 * `usePublicPreviewPrefetch`), its URL goes onto the element and `play()` is called before the tap
 * returns. Once an element has played from a tap, iOS lets it play again without one, so the next
 * track in a queue starts on its own. Without a fetched preview this does nothing and the player
 * starts the track once its URL arrives.
 *
 * The element is registered only while `PublicPreviewPlayer` is mounted, which is exactly while the
 * listener isn't a fully set-up account (`PlayerProvider`): for a set-up account this does nothing,
 * and `publicPreviewActive()` is false.
 */
import type { Track } from "@/types/track";
import { peekPublicPreview } from "@/lib/public-preview";

let element: HTMLAudioElement | null = null;
const listeners = new Set<() => void>();

/** `PublicPreviewPlayer` hands its element over as it mounts, and null as it unmounts. */
export function setPublicPreviewElement(next: HTMLAudioElement | null): void {
  if (element === next) return;
  element = next;
  listeners.forEach((listener) => listener());
}

/** True while the public preview player is mounted: the listener hears public previews. */
export function publicPreviewActive(): boolean {
  return element !== null;
}

/** Calls `listener` whenever publicPreviewActive() may have changed; returns the unsubscribe. */
export function subscribePublicPreviewElement(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Starts `track`'s public preview on the shared element now, when its URL is already known. */
export function startPublicPreviewInTap(track: Pick<Track, "id" | "kind"> | undefined): void {
  const audio = element;
  if (!audio || !track || track.kind === "reference") return;
  const preview = peekPublicPreview(String(track.id));
  if (!preview) return;
  if (audio.getAttribute("src") !== preview.url) audio.src = preview.url;
  // A refusal or a source that can't play reaches the player as the element's own events.
  void audio.play().catch(() => undefined);
}
